import { useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useAppSelector, useComparisonPeriod, ComparisonPeriod, useWeekRange, WeekRange } from 'src/utils/Hooks';
import {
  getComparisonWeekId,
  getPreviousYearWeekId,
  getPreviousWeekId,
  getFirstWeekIdOfYear,
  getLastWeekIdOfYear,
  yearPartOfWeekId,
  getNextWeekId
} from 'src/utils/dateUtils';

/**
 * Generates all week IDs between start and end (inclusive)
 */
const generateWeekIdRange = (startWeekId: number, endWeekId: number): number[] => {
  if (!startWeekId || !endWeekId || startWeekId > endWeekId) {
    return [];
  }

  const weekIds: number[] = [];
  let current = startWeekId;

  while (current <= endWeekId) {
    weekIds.push(current);
    current = getNextWeekId(current);
  }

  return weekIds;
};

/**
 * Return type for useComparisonWeekIds hook
 */
export interface UseComparisonWeekIdsResult {
  /** Start week ID for the primary period */
  startWeekId: number;
  /** End week ID for the primary period */
  endWeekId: number;
  /** Start week ID for the comparison period (API-based) */
  comparisonStartWeekId: number;
  /** End week ID for the comparison period (API-based) */
  comparisonEndWeekId: number;
  /** Whether the comparison week IDs are loading from the API */
  isLoadingComparisonWeeks: boolean;
  /** Whether this is YTD with Prior Year comparison */
  isYTDWithPriorYear: boolean;
  /** Whether this is Last Week with Prior Year comparison */
  isLastWeekWithPriorYear: boolean;
  /**
   * Comparison week IDs that correspond to each main period week ID.
   * These are fetched from the API and properly map 52/53 week years.
   */
  comparisonWeekIdsForMainPeriod: number[];
}

/**
 * Hook that provides comparison week IDs using the microservice API.
 *
 * Uses the same pattern as useForecastComparisonStartAndEndWeekIds:
 * - Fetches comparison weeks from the API using useQuery
 * - Falls back to local calculation if API fails
 * - Uses placeholderData for instant display while loading
 *
 * For Prior Year comparisons, the API provides the correct week mapping
 * that accounts for 52/53 week years.
 */
const UNINITIALIZED_RESULT: UseComparisonWeekIdsResult = {
  startWeekId: 0,
  endWeekId: 0,
  comparisonStartWeekId: 0,
  comparisonEndWeekId: 0,
  isLoadingComparisonWeeks: true,
  isYTDWithPriorYear: false,
  isLastWeekWithPriorYear: false,
  comparisonWeekIdsForMainPeriod: []
};

export const useComparisonWeekIds = (): UseComparisonWeekIdsResult => {
  const weekRange = useWeekRange();
  const comparisonPeriod = useComparisonPeriod();

  // Get main time period from Redux
  const defaultStartWeekId = useAppSelector((state) => state.mainTimePeriod.startWeek);
  const endWeekId = useAppSelector((state) => state.mainTimePeriod.endWeek);
  const defaultComparisonStartWeekId = useAppSelector((state) => state.comparisonTimePeriod.startWeek);
  const defaultComparisonEndWeekId = useAppSelector((state) => state.comparisonTimePeriod.endWeek);

  // Week IDs in YYYYWW format are always > 100000. If they're not set yet,
  // the Redux store hasn't been initialized by computeUpdatedTimePeriods.
  const isTimePeriodInitialized = defaultStartWeekId > 100000 && endWeekId > 100000;

  // Special case: comparing last week with prior year shows YTD data
  const isLastWeekWithPriorYear = useMemo(
    () => weekRange === WeekRange.LAST_WEEK && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR,
    [weekRange, comparisonPeriod]
  );

  // YTD with Prior Year - we show full prior year comparison data
  const isYTDWithPriorYear = useMemo(
    () => weekRange === WeekRange.YTD && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR,
    [weekRange, comparisonPeriod]
  );

  // For Last Week + Prior Year, expand main period to YTD
  const startWeekId = useMemo(() => {
    if (isLastWeekWithPriorYear && endWeekId > 0) {
      return getFirstWeekIdOfYear(yearPartOfWeekId(endWeekId));
    }
    return defaultStartWeekId;
  }, [isLastWeekWithPriorYear, endWeekId, defaultStartWeekId]);

  // Determine if we need to fetch from API (Prior Year comparisons)
  const shouldUsePriorYearApi = comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;

  // Prior Period fallback derived from main period. This is used when Redux comparison
  // range is temporarily invalid (e.g. startWeek > endWeek during period switches).
  const priorPeriodFallbackWeekIds = useMemo(() => {
    if (comparisonPeriod !== ComparisonPeriod.PRIOR_PERIOD) return [];

    return generateWeekIdRange(startWeekId, endWeekId).map((weekId) => getPreviousWeekId(weekId, 1));
  }, [comparisonPeriod, startWeekId, endWeekId]);

  const hasValidReduxComparisonRange =
    defaultComparisonStartWeekId > 0 &&
    defaultComparisonEndWeekId > 0 &&
    defaultComparisonStartWeekId <= defaultComparisonEndWeekId;

  // Memoize placeholder data (local calculation fallback)
  const startPlaceholder = useMemo(
    () => (startWeekId > 0 ? getPreviousYearWeekId(startWeekId) : undefined),
    [startWeekId]
  );
  const endPlaceholder = useMemo(() => (endWeekId > 0 ? getPreviousYearWeekId(endWeekId) : undefined), [endWeekId]);

  // Fetch comparison week for start - use placeholderData for instant display
  const { data: comparisonStartFromApi, isLoading: isLoadingStart } = useQuery({
    queryKey: ['comparisonWeek', 'start', startWeekId],
    queryFn: () => getComparisonWeekId(startWeekId),
    enabled: shouldUsePriorYearApi && startWeekId > 0,
    staleTime: 60 * 60 * 1000, // 1 hour - same as the library cache
    refetchOnWindowFocus: false,
    placeholderData: startPlaceholder
  });

  // Fetch comparison week for end - use placeholderData for instant display
  const { data: comparisonEndFromApi, isLoading: isLoadingEnd } = useQuery({
    queryKey: ['comparisonWeek', 'end', endWeekId],
    queryFn: () => getComparisonWeekId(endWeekId),
    enabled: shouldUsePriorYearApi && endWeekId > 0,
    staleTime: 60 * 60 * 1000, // 1 hour - same as the library cache
    refetchOnWindowFocus: false,
    placeholderData: endPlaceholder
  });

  // Generate all week IDs in the main period for batch fetching
  const mainPeriodWeekIds = useMemo(() => generateWeekIdRange(startWeekId, endWeekId), [startWeekId, endWeekId]);

  // Batch fetch comparison week IDs for all main period weeks
  // This properly handles 52/53 week year mappings through the API
  const comparisonWeekQueries = useQueries({
    queries: mainPeriodWeekIds.map((weekId) => ({
      queryKey: ['comparisonWeek', weekId],
      queryFn: () => getComparisonWeekId(weekId),
      enabled: shouldUsePriorYearApi && weekId > 0,
      staleTime: 60 * 60 * 1000, // 1 hour cache
      refetchOnWindowFocus: false,
      // Use local calculation as placeholder for instant display
      placeholderData: getPreviousYearWeekId(weekId)
    }))
  });

  // Extract the comparison week IDs from the query results
  const comparisonWeekIdsForMainPeriod = useMemo(() => {
    if (!shouldUsePriorYearApi) {
      // For Prior Period, prefer Redux comparison range; fall back to main period shifted back by 1 week.
      const reduxWeekIds = hasValidReduxComparisonRange
        ? generateWeekIdRange(defaultComparisonStartWeekId, defaultComparisonEndWeekId)
        : [];
      if (comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD) {
        return reduxWeekIds.length > 0 ? reduxWeekIds : priorPeriodFallbackWeekIds;
      }

      return reduxWeekIds;
    }

    return comparisonWeekQueries
      .map((query, index) => query.data ?? getPreviousYearWeekId(mainPeriodWeekIds[index]))
      .filter((weekId): weekId is number => weekId !== undefined && weekId > 0);
  }, [
    shouldUsePriorYearApi,
    comparisonWeekQueries,
    mainPeriodWeekIds,
    defaultComparisonStartWeekId,
    defaultComparisonEndWeekId,
    hasValidReduxComparisonRange,
    comparisonPeriod,
    priorPeriodFallbackWeekIds
  ]);

  const isLoadingBatchComparisonWeeks = comparisonWeekQueries.some((query) => query.isLoading);
  const isLoadingComparisonWeeks = isLoadingStart || isLoadingEnd || isLoadingBatchComparisonWeeks;

  // Calculate comparison week IDs
  const comparisonStartWeekId = useMemo(() => {
    if (!shouldUsePriorYearApi) {
      if (hasValidReduxComparisonRange) {
        return defaultComparisonStartWeekId;
      }
      return priorPeriodFallbackWeekIds[0] ?? 0;
    }

    if (isLastWeekWithPriorYear) {
      // Start from the API-calculated comparison for first week of current year
      return comparisonStartFromApi ?? getPreviousYearWeekId(startWeekId);
    }

    // Use API-calculated comparison week, fall back to local calculation
    return comparisonStartFromApi ?? getPreviousYearWeekId(startWeekId);
  }, [
    shouldUsePriorYearApi,
    isLastWeekWithPriorYear,
    comparisonStartFromApi,
    startWeekId,
    defaultComparisonStartWeekId,
    hasValidReduxComparisonRange,
    priorPeriodFallbackWeekIds
  ]);

  const comparisonEndWeekId = useMemo(() => {
    if (!shouldUsePriorYearApi) {
      if (hasValidReduxComparisonRange) {
        return defaultComparisonEndWeekId;
      }
      return priorPeriodFallbackWeekIds[priorPeriodFallbackWeekIds.length - 1] ?? 0;
    }

    if (isYTDWithPriorYear && comparisonStartWeekId > 0) {
      // For YTD + Prior Year, get the last week of the comparison year
      const comparisonYear = yearPartOfWeekId(comparisonStartWeekId);
      return getLastWeekIdOfYear(comparisonYear);
    }

    if (isLastWeekWithPriorYear && endWeekId > 0) {
      // For Last Week + Prior Year, show full prior year
      const comparisonYear = yearPartOfWeekId(endWeekId) - 1;
      return getLastWeekIdOfYear(comparisonYear);
    }

    // Use API-calculated comparison week, fall back to local calculation
    return comparisonEndFromApi ?? getPreviousYearWeekId(endWeekId);
  }, [
    shouldUsePriorYearApi,
    isYTDWithPriorYear,
    isLastWeekWithPriorYear,
    comparisonStartWeekId,
    comparisonEndFromApi,
    endWeekId,
    defaultComparisonEndWeekId,
    hasValidReduxComparisonRange,
    priorPeriodFallbackWeekIds
  ]);

  if (!isTimePeriodInitialized) {
    return UNINITIALIZED_RESULT;
  }

  return {
    startWeekId,
    endWeekId,
    comparisonStartWeekId,
    comparisonEndWeekId,
    isLoadingComparisonWeeks,
    isYTDWithPriorYear,
    isLastWeekWithPriorYear,
    comparisonWeekIdsForMainPeriod
  };
};

export default useComparisonWeekIds;
